package br.com.texeratx.boardgametimeapp.ui.screens.options

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material3.Button
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.core.i18n.AppLanguage
import br.com.texeratx.boardgametimeapp.core.i18n.LanguageManager
import br.com.texeratx.boardgametimeapp.core.i18n.LocalStrings
import br.com.texeratx.boardgametimeapp.core.spotify.isSpotifySupportedOnCurrentPlatform
import br.com.texeratx.boardgametimeapp.core.spotify.auth.SpotifyAuthManager
import br.com.texeratx.boardgametimeapp.core.theme.AppThemeMode
import br.com.texeratx.boardgametimeapp.core.theme.ThemeManager
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppScreenScaffold
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppSectionCard
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppTopBarColors
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import kotlinx.coroutines.launch
import org.koin.compose.koinInject

class OptionsScreen : Screen {

    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val uriHandler = LocalUriHandler.current
        val spotifyAuth = koinInject<SpotifyAuthManager>()
        val languageManager = koinInject<LanguageManager>()
        val themeManager = koinInject<ThemeManager>()

        val currentLanguage by languageManager.language.collectAsState()
        val currentTheme by themeManager.theme.collectAsState()
        val scope = rememberCoroutineScope()
        val spotifySupported = isSpotifySupportedOnCurrentPlatform()

        OptionsUI(
            onBack = { navigator.pop() },
            onConnectSpotify = {
                val url = spotifyAuth.buildAuthorizeUrl()
                uriHandler.openUri(url)
            },
            spotifySupported = spotifySupported,
            onOpenTerms = {
                uriHandler.openUri(legalUrl(path = "terms", language = currentLanguage))
            },
            onOpenPrivacy = {
                uriHandler.openUri(legalUrl(path = "privacy", language = currentLanguage))
            },
            currentLanguage = currentLanguage,
            onLanguageSelected = { lang ->
                scope.launch { languageManager.setLanguage(lang) }
            },
            currentTheme = currentTheme,
            onThemeSelected = { mode ->
                scope.launch { themeManager.setTheme(mode) }
            },
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun OptionsUI(
    onBack: () -> Unit,
    onConnectSpotify: () -> Unit,
    spotifySupported: Boolean,
    onOpenTerms: () -> Unit,
    onOpenPrivacy: () -> Unit,
    currentLanguage: AppLanguage,
    onLanguageSelected: (AppLanguage) -> Unit,
    currentTheme: AppThemeMode,
    onThemeSelected: (AppThemeMode) -> Unit,
) {
    val S = LocalStrings.current

    AppScreenScaffold(
        topBar = {
            TopAppBar(
                title = { Text(S.optionsTitle, fontWeight = FontWeight.Bold) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = S.back)
                    }
                },
                colors = AppTopBarColors(),
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier
                .padding(padding)
                .padding(16.dp)
                .fillMaxSize(),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Text(S.themeTitle, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)

            AppSectionCard(modifier = Modifier.fillMaxWidth()) {
                AppThemeMode.entries.forEach { mode ->
                    val isDefault = mode == AppThemeMode.DEFAULT
                    val title = if (isDefault) S.themeDefault else S.themeShadow
                    val description = if (isDefault) S.themeDefaultDescription else S.themeShadowDescription

                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        RadioButton(
                            selected = currentTheme == mode,
                            onClick = { onThemeSelected(mode) }
                        )
                        Column(modifier = Modifier.padding(start = 8.dp)) {
                            Text(text = title, style = MaterialTheme.typography.bodyLarge)
                            Text(
                                text = description,
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                            )
                        }
                    }
                }
            }

            Spacer(Modifier.height(8.dp))

            Text(S.languageTitle, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)

            AppSectionCard(modifier = Modifier.fillMaxWidth()) {
                AppLanguage.entries.forEach { lang ->
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        RadioButton(
                            selected = currentLanguage == lang,
                            onClick = { onLanguageSelected(lang) }
                        )
                        Text(
                            text = lang.displayName,
                            modifier = Modifier.padding(start = 8.dp)
                        )
                    }
                }
            }

            Spacer(Modifier.height(8.dp))

            Text(S.connectionsTitle, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)

            AppSectionCard(modifier = Modifier.fillMaxWidth()) {
                Button(
                    onClick = onConnectSpotify,
                    enabled = spotifySupported,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(S.connectSpotify)
                }
                if (!spotifySupported) {
                    Spacer(Modifier.height(8.dp))
                    Text(
                        text = S.spotifyOnlyInApp,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }

            Spacer(Modifier.height(8.dp))

            Text(S.termsTitle, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)

            AppSectionCard(modifier = Modifier.fillMaxWidth()) {
                Button(
                    onClick = onOpenTerms,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(S.termsOfUseSoon)
                }
                Spacer(Modifier.height(10.dp))
                Button(
                    onClick = onOpenPrivacy,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(S.privacyPolicySoon)
                }
            }
        }
    }
}

private const val LEGAL_BASE_URL = "https://boardgametimerbackend-production.up.railway.app/legal"

private fun legalUrl(path: String, language: AppLanguage): String {
    val lang = if (language == AppLanguage.EN) "en" else "pt-BR"
    return "$LEGAL_BASE_URL/$path?lang=$lang"
}
